import React, { useEffect, useRef, useState } from 'react';
import type { GameRule } from '../types';
import { rulesToMarkdown } from '../utils/ruleMarkdown';
import { RuleSections } from './RuleSections';

interface RulesMarkdownEditorProps {
  rules: GameRule[];
  onSave: (markdown: string) => Promise<void>;
}

export function RulesMarkdownEditor({ rules, onSave }: RulesMarkdownEditorProps) {
  const source = rulesToMarkdown(rules);
  const [markdown, setMarkdown] = useState(source);
  const [base, setBase] = useState(source);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (dirtyRef.current) return;
    setMarkdown(source);
    setBase(source);
  }, [source]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(markdown);
      dirtyRef.current = false;
      setBase(markdown);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regeln konnten nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rule-markdown">
      <label className="rule-markdown-label" htmlFor="rules-markdown">
        Regeln als Markdown
        <textarea
          id="rules-markdown"
          className="rule-markdown-input"
          value={markdown}
          disabled={busy}
          spellCheck
          placeholder={'# Der Spielplan\n\n## Das Dorf\nHier startet man.\n\n## Der Friedhof\nHier wandern Untote.'}
          onChange={event => {
            dirtyRef.current = true;
            setMarkdown(event.target.value);
          }}
        />
      </label>
      <p className="form-help-text">
        Eine Hauptüberschrift mit # wird ein Aufklapp-Punkt. Kleinere Überschriften mit ## und ### stehen darin als Abschnitte. Darunter gehen Absätze, Listen, **fett** und *kursiv*.
      </p>
      <div className="rule-markdown-actions">
        <button type="button" className="medieval-button" onClick={() => void save()} disabled={busy || markdown === base}>
          {busy ? 'Speichert…' : 'Regeln speichern'}
        </button>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <h3 className="rule-markdown-preview-title">Vorschau</h3>
      <RuleSections markdown={markdown} emptyText="Schreib eine Überschrift, dann erscheint hier ein Punkt." />
    </div>
  );
}
